import React from 'react';
import { CircularProgress, Fab } from '@material-ui/core';
import { Stage, Layer } from 'react-konva';
import PropTypes from 'prop-types';

// Icons
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import PanToolIcon from '@material-ui/icons/PanTool';
import DoneIcon from '@material-ui/icons/Done';

// Components
import KonvaImage from './KonvaImage';

// Constants
import { T } from '../constants/tokens';

/**
 * The konva stage the floor plan and its room zones are drawn on.
 *
 * The stage measures the element it sits in rather than guessing from the
 * window. It used to take `windowSize.width * 8 / 12` and
 * `windowSize.height - 200`, numbers tuned to a shell that no longer exists —
 * inside the rail, the 1200px content column and the detail aside, they were
 * always wrong, and the map drew off the edge of its own card.
 *
 * Props: height, onRoomSelection, onClick
 */
class MapPanel extends React.Component {

  constructor(props) {
    super(props);

    this.containerRef = React.createRef();
    this.resizeObserver = null;

    this.state = {
      stageWidth: 0,
      stageHeight: 0,
      inPanMode: false,
      stageScale: 1,
      layerScale: 1,
      layerOffsetX: 0,
      layerOffsetY: 0,
      imageRef: null,
      dragStartX: null,
      dragStartY: null,
      dragX: 0,
      dragY: 0
    };

    this.stage = null;
  }

  componentDidMount() {
    this.measure();

    // ResizeObserver catches the rail appearing, the aside collapsing and the
    // window resizing alike, which a window listener alone would not.
    if (typeof ResizeObserver !== 'undefined' && this.containerRef.current) {
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.containerRef.current);
    }
  }

  componentWillUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  measure = () => {
    const node = this.containerRef.current;
    if (node == null) return;
    // Inside the border. The bounding box includes it, and a panel sized by
    // its content (a centred flex column) then grew by the border on every
    // measure, without end.
    const width = node.clientWidth;
    const height = node.clientHeight;
    if (width !== this.state.stageWidth || height !== this.state.stageHeight) {
      // Refit the floor plan to the new size, not only when it first loads:
      // it may load while the panel is still being laid out.
      this.setState({ stageWidth: width, stageHeight: height }, this.fitImage);
    }
  }

  togglePanMode = () => {
    this.setState({ inPanMode: !this.state.inPanMode });
  }

  /** A click on the plan, in floor plan pixels. Ignored while panning. */
  onStageClick = () => {
    if (!this.props.onClick || this.state.inPanMode || this.stageRef == null) return;
    const x = Math.floor((this.stageRef.getPointerPosition().x - this.state.dragX) / (this.state.layerScale * this.state.stageScale) + this.state.layerOffsetX);
    const y = Math.floor((this.stageRef.getPointerPosition().y - this.state.dragY) / (this.state.layerScale * this.state.stageScale) + this.state.layerOffsetY);
    this.props.onClick(x, y);
  }

  zoomIn = () => {
    this.setState({ stageScale: this.state.stageScale + 0.1 });
  }

  zoomOut = () => {
    this.setState({ stageScale: this.state.stageScale > 0.3 ? this.state.stageScale - 0.1 : 0.3 });
  }

  onImageLoad = (imageRef) => {
    this.imageSize = { width: imageRef.attrs.image.width, height: imageRef.attrs.image.height };
    this.fitImage();
  }

  /** Scale and centre the floor plan to fill the panel. */
  fitImage = () => {
    // Before the panel is measured there is nothing to fit to; measuring
    // calls this again.
    if (this.imageSize == null || this.state.stageWidth <= 40 || this.state.stageHeight <= 40) return;
    const { width, height } = this.imageSize;
    const newMaxWidth = this.state.stageWidth - 40;
    const newMaxHeight = this.state.stageHeight - 40;

    // Make the Image the Size of the Panel
    var widthScale = width && newMaxWidth && width !== 0 ? newMaxWidth / width : 0;
    var heightScale = height && newMaxHeight && height !== 0 ? newMaxHeight / height : 0;

    var scale = Math.min(widthScale, heightScale);
    scale = scale === 0 ? 1 : scale;
    this.setState({ layerScale: scale });

    // Centre the plan. A layer's offset is in its own, unscaled units, so the
    // spare screen space is divided by the scale; in screen pixels it was
    // only right when the plan was drawn near 100%.
    this.setState({
      layerOffsetX: -((this.state.stageWidth - scale * width) / 2) / scale,
      layerOffsetY: -((this.state.stageHeight - scale * height) / 2) / scale
    });
  }

  onDragStart = () => {
    const stage = this.stageRef;
    this.setState({
      dragStartX: stage.x(),
      dragStartY: stage.y()
    });
  }

  onDragEnd = () => {
    const stage = this.stageRef;
    const dragEndX = stage.x();
    const dragEndY = stage.y();
    const distanceX = dragEndX - this.state.dragStartX;
    const distanceY = dragEndY - this.state.dragStartY;
    this.setState({ dragX: this.state.dragX + distanceX, dragY: this.state.dragY + distanceY });
  }

  render() {
    return (
      <div
        ref={this.containerRef}
        style={{
          position: 'relative',
          background: T.surfaceSoft,
          border: '1px solid ' + T.hairline,
          borderRadius: 6,
          overflow: 'hidden',
          width: '100%',
          height: this.props.height ?? 520,
          ...this.props.style
        }}
      >
        {this.props.isLoading && <CircularProgress />}
        {/* Not until measured: konva throws drawing a canvas of size 0. */}
        {this.state.stageWidth > 0 && this.state.stageHeight > 0 &&
        <Stage
          onClick={this.onStageClick}
          onTap={this.onStageClick}
          width={this.state.stageWidth}
          height={this.state.stageHeight}
          scaleX={this.state.stageScale}
          scaleY={this.state.stageScale}
          draggable={this.state.inPanMode}
          onDragStart={this.onDragStart}
          onDragEnd={this.onDragEnd}
          ref={ref => {
            this.stageRef = ref; 
          }}>

          <Layer scaleX={this.state.layerScale} scaleY={this.state.layerScale} offsetX={this.state.layerOffsetX} offsetY={this.state.layerOffsetY}>
            {this.props.floorPlanImagePath != null &&
            <KonvaImage src={this.props.floorPlanImagePath} scale={this.state.layerScale} onLoad={this.onImageLoad} />}
          </Layer>

          <Layer scaleX={this.state.layerScale} scaleY={this.state.layerScale} offsetX={this.state.layerOffsetX} offsetY={this.state.layerOffsetY}>
            {/* Children may be a function of the on-screen scale (fit times
                zoom), for anything that must stay a fixed size on screen
                however large the floor plan image or however far it is
                zoomed. Konva does not carry React context into the canvas. */}
            {typeof this.props.children === 'function'
              ? this.props.children(this.state.layerScale * this.state.stageScale)
              : this.props.children}
          </Layer>
        </Stage>}

        <div style={{ position: 'absolute', right: 8, bottom: 8 }}>
          <Fab style={{ marginRight: 8, width: 36, height: 36, minHeight: 36 }} onClick={this.togglePanMode}>
            {!this.state.inPanMode && <PanToolIcon style={{ width: 16, height: 16 }} />}
            {this.state.inPanMode && <DoneIcon style={{ width: 16, height: 16 }} />}
          </Fab>
          <Fab style={{ marginRight: 8, width: 36, height: 36, minHeight: 36 }} onClick={this.zoomIn} disabled={this.state.stageScale >= 2}>
            <ZoomInIcon style={{ width: 16, height: 16 }} />
          </Fab>
          <Fab style={{ width: 36, height: 36, minHeight: 36 }} onClick={this.zoomOut} disabled={this.state.stageScale <= 0.3 }>
            <ZoomOutIcon style={{ width: 16, height: 16 }} />
          </Fab>
        </div>
      </div>
    );
  }
}

export default MapPanel;

MapPanel.propTypes = {
  onClick: PropTypes.func,
  height: PropTypes.number,
  style: PropTypes.object,
  isLoading: PropTypes.bool,
  floorPlanImagePath: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.node, PropTypes.func])
};
